import { api, publicRequest, tokenStore } from '@/lib/api';
import type { AuthTokens, AuthUser } from '@/types/api';

/* ============================================================================
 * Auth API surface mirrors backend/auth_otp/views.py and admin/views.py
 * Keep the request shapes in lockstep with the DRF serializers.
 * ==========================================================================*/

export interface RequestOtpPayload {
  email: string;
  name?: string;
  role?: 'job_seeker' | 'employer';
}

export interface VerifyOtpPayload {
  email: string;
  otp: string;
}

export interface SetPasswordPayload {
  email: string;
  password: string;
  temp_token: string;
}

export interface LoginPayload {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: AuthUser;
  // Tokens arrive nested (login, oauth callbacks) or top-level (set-password).
  tokens?: { access: string; refresh: string };
  access?: string;
  refresh?: string;
  // `role` / `profile_complete` are returned alongside `user`, not inside it.
  role?: AuthUser['role'];
  profile_complete?: boolean;
}

export const authApi = {
  requestOtp: (payload: RequestOtpPayload) =>
    publicRequest<{ message: string; expires_at: string }>('/auth/request-otp/', {
      method: 'POST',
      data: payload,
    }),

  verifyOtp: (payload: VerifyOtpPayload) =>
    publicRequest<{ verified: boolean; temp_token: string }>('/auth/verify-otp/', {
      method: 'POST',
      data: payload,
    }),

  resendOtp: (payload: { email: string }) =>
    publicRequest<{ message: string }>('/auth/resend-otp/', {
      method: 'POST',
      data: payload,
    }),

  setPassword: (payload: SetPasswordPayload) =>
    publicRequest<LoginResponse>('/auth/set-password/', {
      method: 'POST',
      data: payload,
    }),

  login: (payload: LoginPayload) =>
    publicRequest<LoginResponse>('/auth/login/', { method: 'POST', data: payload }),

  me: () => api.get<AuthUser>('/auth/me/').then((r) => r.data),

  logout: async () => {
    const refresh = tokenStore.getUser()?.refresh;
    try {
      await api.post('/auth/logout/', refresh ? { refresh } : {});
    } finally {
      tokenStore.setUser(null);
    }
  },

  forgotPasswordRequest: (payload: { email: string }) =>
    publicRequest<{ message: string }>('/auth/forgot-password/request/', {
      method: 'POST',
      data: payload,
    }),

  forgotPasswordVerify: (payload: { email: string; otp: string }) =>
    publicRequest<{ reset_token: string }>('/auth/forgot-password/verify/', {
      method: 'POST',
      data: payload,
    }),

  forgotPasswordReset: (payload: {
    email: string;
    reset_token: string;
    password: string;
    password2: string;
  }) =>
    publicRequest<{ message: string }>('/auth/forgot-password/reset/', {
      method: 'POST',
      data: payload,
    }),

  /** Browser-redirect entry point for an OAuth provider (backend handles the dance). */
  oauthStartUrl: (provider: 'google' | 'linkedin', redirectUri: string) =>
    `/auth/oauth/${provider}/login/?redirect_uri=${encodeURIComponent(redirectUri)}`,

  /** Finalize an OAuth signup once a new user has chosen a role. `ticket` is the
   *  signed value from the OAuth callback redirect (tally-backend#89). */
  oauthCompleteRole: (payload: { ticket: string; role: 'job_seeker' | 'employer' }) =>
    publicRequest<LoginResponse>('/auth/oauth/complete/', { method: 'POST', data: payload }),

  /** Swap an existing user's OAuth login ticket for the session (tally-backend#99):
   *  the callback redirect carries no tokens. */
  oauthExchange: (ticket: string) =>
    publicRequest<LoginResponse>('/auth/oauth/exchange/', { method: 'POST', data: { ticket } }),

  /* -- Authenticated: step-up authorization OTP for sensitive actions ------ */
  authorizationSendOtp: () =>
    api.post<{ success: boolean; message: string }>('/auth/authorization/send-otp/').then(
      (r) => r.data,
    ),
  authorizationVerifyOtp: (payload: { otp: string }) =>
    api
      .post<{ success: boolean; message: string }>('/auth/authorization/verify-otp/', payload)
      .then((r) => r.data),

  /* -- Authenticated: dual-OTP email change (4 steps + cancel) ------------- */
  emailChangeIdentityRequest: () =>
    api.post<{ message: string; expires_at: string }>('/auth/email/change/identity/request/').then(
      (r) => r.data,
    ),
  emailChangeIdentityVerify: (payload: { otp: string }) =>
    api
      .post<{ change_token: string; expires_at: string }>(
        '/auth/email/change/identity/verify/',
        payload,
      )
      .then((r) => r.data),
  emailChangeNewRequest: (payload: { new_email: string; change_token: string }) =>
    api.post<{ message: string; expires_at: string }>('/auth/email/change/new/request/', payload).then(
      (r) => r.data,
    ),
  emailChangeNewVerify: (payload: { new_email: string; otp: string; change_token: string }) =>
    api.post<LoginResponse>('/auth/email/change/new/verify/', payload).then((r) => r.data),
  emailChangeCancel: () =>
    api.post<{ message: string }>('/auth/email/change/cancel/').then((r) => r.data),

  /* -- Authenticated: change password -------------------------------------- */
  changePassword: (payload: {
    current_password: string;
    new_password: string;
    confirm_password: string;
  }) => api.post<{ message: string }>('/auth/change-password/', payload).then((r) => r.data),
};

/** Save tokens + user atomically after a successful login response. */
export function applyLoginResponse(response: LoginResponse): {
  user: AuthUser;
  tokens: AuthTokens;
} {
  const access = response.tokens?.access ?? response.access;
  const refresh = response.tokens?.refresh ?? response.refresh;
  if (!access || !refresh) {
    throw new Error('Login response did not include authentication tokens.');
  }
  const tokens: AuthTokens = { access, refresh };
  tokenStore.setUser(tokens);
  // `role` and `profile_complete` live at the top level of the response;
  // fold them onto the user so routing and onboarding gates see them.
  const user: AuthUser = {
    ...response.user,
    role: response.user.role ?? response.role ?? ('job_seeker' as AuthUser['role']),
    profile_complete: response.profile_complete ?? response.user.profile_complete,
  };
  return { user, tokens };
}
